import { AppForms, React, variantProvider } from '@/app'
import { ActivityIndicator, AuthFormComponents, Button, Checkbox, Link, Text, TextInput, View } from '@/components'
import { AppStatus, ModalName, useAppSelector } from '@/redux'
import { APIClient } from '@/services'
import { useIsMobile, useKeydown } from '@/utils'
import { assignTextStyle, useForm, useI18N } from '@codeleap/common'
import { navigate } from 'gatsby'

type SignupFormProps = {
  isModal?: boolean
  title?: string
  modalName?: ModalName
  referral_code?: string
}

const BREAKPOINT = 'mid'

export const SignupForm = (props: SignupFormProps) => {
  const { isModal = true, referral_code = '', modalName = 'signup' } = props

  const isFocused = useAppSelector(state => state?.AppStatus?.modals?.signup)

  const [signUpSource, setSignUpSource] = React.useState('email')
  const isEmail = signUpSource === 'email'
  const isSocial = signUpSource !== 'email'

  const isMobile = useIsMobile()

  const areFieldsVisible = isModal ? isFocused : true
  const formConfig = isEmail ? AppForms.signup(areFieldsVisible) : AppForms.socialSignup(areFieldsVisible)

  const form = useForm(formConfig, { initialState: { referral_code }})

  const referralCode = APIClient.Session.useReferralCode(form.values.referral_code)

  React.useEffect(() => {
    if (referral_code) form.setFieldValue('referral_code', referral_code)
  }, [referral_code])

  const handleSocial = APIClient.Session.useSocialLogin(
    (userSocial, provider) => {
      if (userSocial) {
        AppStatus.set('loading')

        form.setFormValues(userSocial)
        setSignUpSource(provider)

        setTimeout(() => {
          handleSubmit(true, userSocial)
        }, 1000)
      }
    },
    modalName as 'login' | 'signup',
  )

  const { t } = useI18N()

  const { signup } = APIClient.Session.useSignup()

  const handleSubmit = async (withSocial = false, userValues = null) => {
    if (!referralCode.isValid) return

    const { name, email, ...rest } = userValues || form.values
    const firstName = name.split(' ').slice(0, 1).join(' ')
    const lastName = name.split(' ').slice(1).join(' ')
    const values = { ...rest, email: email.toLowerCase(), first_name: firstName, last_name: lastName }

    const result = await signup(values, signUpSource as any)

    if (!!result?.user) {
      if (withSocial) {
        AppStatus.set('idle')
      }

      console.log('Exibindo modal hereWeGo', { isModal, withSocial })

      if (isModal) {
        AppStatus.transitionModals([modalName, 'hereWeGo'])
      } else {
        AppStatus.setModal('hereWeGo')
      }
    }
  }

  const openLogin = () => {
    if (isModal) {
      AppStatus.transitionModals([modalName, 'login'])
    } else {
      navigate('/auth/login/')
    }
  }

  useKeydown(
    () => {
      const isInvalidSubmit = (isModal && !isFocused) || !form.isValid
      if (isInvalidSubmit) return
      handleSubmit?.()
    },
    [isFocused, isModal, form?.values],
    'enter',
  )

  return (
    <>
      <View variants={['flex', 'column', 'gap:4', 'fullWidth', 'marginTop:2']}>
        <View variants={['column', 'gap:1']}>
          <View variants={['row', 'gap:4']} responsiveVariants={{ [BREAKPOINT]: ['column', 'gap:0'] }}>
            <TextInput {...form.register('name')} />
            <TextInput {...form.register('email')} leftIcon={{ name: 'mail' }} disabled={isSocial} />
          </View>
          {isSocial ? null : (
            <View variants={['row', 'gap:4', 'alignEnd']} responsiveVariants={{ [BREAKPOINT]: ['column', 'gap:0'] }}>
              <TextInput {...form.register('password')} leftIcon={{ name: 'key' }} visibilityToggle />
              <TextInput {...form.register('repeatPassword')} leftIcon={{ name: 'key' }} visibilityToggle />
            </View>
          )}
          <View variants={['row', 'gap:4']} responsiveVariants={{ [BREAKPOINT]: ['column', 'gap:0'] }}>
            <TextInput
              {...form.register('referral_code')}
              leftIcon={{ name: 'form-input' }}
              debugName={'Referral code input'}
              styles={{ wrapper: styles.singleInput }}
              _error={!referralCode.isValid && t('form.validations.invalidReferralCode')}
              rightIcon={
                referralCode.isLoading && !referralCode.isValid ? (
                  <ActivityIndicator variants={['medium']} />
                ) : undefined
              }
            />

            {!isMobile ? <View style={{ width: '50%' }} /> : null}
          </View>

          <Checkbox {...form.register('receive_marketing_email')} variants={['left']} />
        </View>

        <View variants={['alignSelfCenter']}>
          <Text variants={['p3', 'color:neutral8']}>
            {t('Login.disclaimer')}
            <Link to='https://hurd.world/terms-of-use' text={t('termsOfUse')} style={styles.link} tabIndex={0} />
            {', '}
            <Link to='https://hurd.world/privacy-policy' text={t('privacyPolicy')} style={styles.link} tabIndex={0} />
            {t('Login.disclaimer2')}
            <Link
              to='https://hurd.world/community-guidelines'
              text={' ' + t('communityGuidelines')}
              style={styles.link}
              tabIndex={0}
            />
          </Text>
        </View>

        <AuthFormComponents.SocialProviders onPress={handleSocial} />

        <View variants={['column', 'gap:2', 'alignSelfCenter']}>
          <Button
            onPress={handleSubmit}
            disabled={!form.isValid || !referralCode.isValid}
            debugName={`submit Signup`}
            text={t('SignUp.createAccount')}
            variants={[
              'marginHorizontal:auto',
              'fullWidth',
              'large',
              'pill',
              'paddingHorizontal:2',
              !form.isValid && 'outline',
            ]}
            aria-label={t('SignUp.createAccount')}
            aria-invalid={!form.isValid}
          />
          <Button
            aria-label={`${t('SignUp.txt4')} ${t('SignUp.login')}`}
            onPress={openLogin}
            debugName={'Already have account Button'}
            variants={[
              'outline',
              'pill',
              'large',
              'row',
              'gap:0.5',
              'alignCenter',
              'justifyCenter',
              'paddingHorizontal:2',
              'bg:transparent',
            ]}
          >
            <Text text={t('SignUp.txt4')} variants={['medium', 'alignSelfCenter', 'row', 'color:neutral10']} />
            <Text text={t('SignUp.login')} variants={['medium', 'color:primary3', 'inline', 'marginRight:0.5']} />
          </Button>
        </View>
      </View>
    </>
  )
}

const styles = variantProvider.createComponentStyle(
  theme => ({
    link: {
      ...assignTextStyle('p3')(theme).text,
      color: theme.colors.primary3,
      fontWeight: '400',
      textDecoration: 'none',
    },
    singleInput: {
      width: '50%',

      [theme.media.down(BREAKPOINT)]: {
        width: '100%',
      },
    },
  }),
  true,
)
